/* eslint-disable camelcase */
import isEqual from 'lodash/isEqual';
import { cleanObjectFromUndefined } from './clean-object-from-undefined';
import dayjs from '../config/dayjs';

// This method unpacks an iterator and creates an object
function paramsToObject(entries) {
  const result = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
}

const getSerializedSearch = (searchObject) => {
  const copy = { ...searchObject };
  Object.keys(copy).forEach((key) => {
    // eslint-disable-next-line no-param-reassign
    copy[key] = JSON.stringify(copy[key]);
  });
  return `${new URLSearchParams(copy).toString()}`;
};

// This method seeks to push filter information to URL
const filterUrlMerger = (history, commonVarsCopy, filtersFromLocation) => {
  if (isEqual(commonVarsCopy, filtersFromLocation)) {
    return;
  }
  const search = getSerializedSearch(commonVarsCopy);
  history.replace({
    pathname: history.location.pathname,
    search,
  });
};

// This is to obtain the current search Object (pre serialization)
const getCurrentSearchObject = (history) => {
  const { search } = history.location;
  if (!search) {
    return {};
  }
  return paramsToObject(new URLSearchParams(search).entries());
};

// This method seeks to retrive filters from URL
export const getFiltersFromHistory = (history) => {
  const { utm_campaign, apcid, ...filtersFromLocation } = getCurrentSearchObject(history);
  Object.keys(cleanObjectFromUndefined(filtersFromLocation)).forEach((key) => {
    try {
      filtersFromLocation[key] = JSON.parse(filtersFromLocation[key]);
    } catch (error) {
      // If the url parame is not in JSON format, it is ignored
    }
  });
  return filtersFromLocation;
};

export const clearFiltersFromHistory = (history) => {
  history.replace({
    pathname: history.location.pathname,
    search: '',
  });
};

// This method seeks to retrive one filter from the URL
const getFilterByNameFromHistory = (history, name) => getFiltersFromHistory(history)[name];

// This method seeks to synchronize URL with Filters
const historyToFilters = (history, commonVars) => {
  const filtersFromLocation = getFiltersFromHistory(history);
  const desiredFilters = cleanObjectFromUndefined(commonVars);
  filterUrlMerger(history, desiredFilters, filtersFromLocation);
};

// Merge updates into current URL filters (preserves keys not present in updates)
export const patchUrlFilters = (history, updates) => {
  const next = { ...getFiltersFromHistory(history), ...updates };
  Object.keys(updates).forEach((key) => {
    if (updates[key] === null || updates[key] === undefined || updates[key] === '') {
      delete next[key];
    }
  });
  historyToFilters(history, next);
};

// Pre-render use method to clean url
const getAndEraseFiltersFromHistory = (history, ...params) => {
  const updatedSearch = { ...getFiltersFromHistory(history) };
  const deletedValues = {};
  params.forEach((key) => {
    deletedValues[key] = updatedSearch[key];
    delete updatedSearch[key];
  });
  filterUrlMerger(history, updatedSearch, {});
  return deletedValues;
};

const getDefaultDateIssuedGte = (initialInvoiceFilters) => {
  if (initialInvoiceFilters?.dateIssued_Gte) {
    return dayjs(initialInvoiceFilters.dateIssued_Gte, 'YYYY-MM-DD');
  }
  return dayjs().subtract(2, 'months');
};

const getDefaultFiltersFromHistory = (history, initialOrderBy, initialInvoiceFilters) => {
  const {
    first,
    offset,
    globalFilter,
    dateIssued_Gte,
    dateIssued_Lte,
    receiver_Rut,
    company_MasterEntity_Rut,
    dteType_Code_In,
    dfstateIn,
    allReceiverCompany,
    allIssuedCompany,
    documentType,
    orderBy,
    ...rest
  } = getFiltersFromHistory(history);
  return {
    defaultPageSize: first || 30,
    defaultPage: first && offset ? offset / first : 0,
    defaultDates: {
      dateIssued_Gte: dateIssued_Gte
        ? dayjs(dateIssued_Gte, 'YYYY-MM-DD')
        : getDefaultDateIssuedGte(initialInvoiceFilters),
      dateIssued_Lte: dateIssued_Lte
        ? dayjs(dateIssued_Lte, 'YYYY-MM-DD')
        : dayjs(),
    },
    defaultGlobalFilter: globalFilter || '',
    defaultOrderBy: orderBy || initialOrderBy,
    defaultFilters: rest,
  };
};

export {
  getCurrentSearchObject,
  getDefaultFiltersFromHistory,
  getAndEraseFiltersFromHistory,
  getFilterByNameFromHistory,
  getSerializedSearch,
  historyToFilters,
  paramsToObject,
};
